import {type IconName} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import Color from 'color';
import React, {
  type CSSProperties,
  memo,
  type ReactElement,
  useMemo,
} from 'react';
import {type ColorResult, SketchPicker} from 'react-color';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, Popup} from 'semantic-ui-react';

import {
  ColorPaletteOption,
  getColorName,
  getColorPaletteSpectrumOrder,
} from '../util/colors';

export type ColorPickerTriggerRenderProps = {
  activeColor: string;
  icon: IconName;
  onBlur: () => void;
  onClick: () => void;
  onFocus: () => void;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
};

type TriggerRenderDelegate = (
  params: ColorPickerTriggerRenderProps
) => ReactElement;

type ColorPickerTriggerProps = ColorPickerTriggerRenderProps & {
  invertColorsInDarkMode: boolean;
  readOnly: boolean;
  style?: CSSProperties;
  trigger: 'icon' | 'button' | TriggerRenderDelegate;
};

function ColorPickerTrigger({
  activeColor,
  icon,
  invertColorsInDarkMode,
  readOnly,
  style,
  trigger,
  ...handlers
}: ColorPickerTriggerProps) {
  if (trigger === 'icon') {
    return (
      <Icon
        data-test="color-picker-trigger"
        style={{
          ...style,
          opacity: 1,
          color: activeColor,
          cursor: readOnly ? 'default' : 'pointer',
        }}
        className={invertColorsInDarkMode ? '' : 'night-aware'}
        name="circle"
        title={getColorName(activeColor) || activeColor}
        {...handlers}
      />
    );
  }
  if (trigger === 'button') {
    return (
      // The Button component's `icon` prop can only take an IconName (not a semantic-ui icon). I can
      // use the semantic-ui icon as a child like <Button><Icon /></Button> but the icon's width and height are weird
      // and difficult to center.
      // I tried using the IconFilledCircle component as a child too but it has its own internal padding,
      // which makes it difficult to get the size right.
      // I also tried just passing in `filled-circle` as the icon prop, but the `Button` component doesn't allow
      // me to style a passed in icon either.
      // So I'm just using regular divs and making a circle shape.
      <div
        className="night-aware flex h-32 w-32 items-center justify-center rounded bg-transparent hover:bg-oblivion/[0.07] dark:hover:bg-moonbeam/[0.09]" // Same background color as `Button` in ghost variant on hover.
        role="button"
        {...handlers}>
        <div
          title={getColorName(activeColor) || activeColor}
          data-test="color-picker-trigger"
          className="h-12 w-12 rounded-full"
          style={{backgroundColor: activeColor}}
        />
      </div>
    );
  }
  return trigger({activeColor, icon, ...handlers});
}

export interface ColorPickerProps {
  activeColor: string;
  colorPaletteOption?: ColorPaletteOption;
  containerStyle?: CSSProperties;
  invertColorsInDarkMode?: boolean; // whether to keep the colors the same in dark mode, or invert them.
  readOnly?: boolean; // don't open on click
  setColor?: (color: string) => void;
  style?: CSSProperties;
  trigger?: 'button' | 'icon' | TriggerRenderDelegate;
  triggerIcon?: IconName;
}

const ColorPicker = memo((props: ColorPickerProps) => {
  const {
    activeColor,
    colorPaletteOption,
    containerStyle,
    invertColorsInDarkMode = true,
    readOnly,
    setColor,
    style,
    trigger = 'icon',
    triggerIcon = 'circle-filled',
  } = props;

  const handleChangeComplete = (color: ColorResult) => {
    const {a, ...colorMinusAlpha} = color.rgb;
    const alpha = a || 0.8;
    const updatedColor = Color(colorMinusAlpha).alpha(alpha).string();
    if (setColor != null) {
      setColor(updatedColor);
    }
  };

  const effectiveReadOnly = readOnly ?? false;
  const presetColors = useMemo(
    () => [
      ...getColorPaletteSpectrumOrder(
        colorPaletteOption ?? ColorPaletteOption.DEFAULT
      ),
    ],
    [colorPaletteOption]
  );

  return (
    <Popup
      openOnTriggerClick={!effectiveReadOnly}
      trigger={
        // @ts-expect-error Missing props added by semantic-ui-react's Popup
        <ColorPickerTrigger
          activeColor={activeColor}
          icon={triggerIcon}
          invertColorsInDarkMode={invertColorsInDarkMode}
          readOnly={effectiveReadOnly}
          style={style}
          trigger={trigger}
        />
      }
      content={
        invertColorsInDarkMode ? (
          <SketchPicker
            color={activeColor}
            presetColors={presetColors}
            onChangeComplete={handleChangeComplete}
          />
        ) : (
          <TailwindContents>
            <SketchPicker
              className="night-aware text-moon-600 dark:text-moon-200 [&_input]:bg-inherit [&_input]:text-inherit [&_label]:!text-inherit"
              styles={{
                default: {
                  picker: {
                    backgroundColor: 'inherit',
                  },
                },
              }}
              color={activeColor}
              presetColors={presetColors}
              onChangeComplete={handleChangeComplete}
            />
          </TailwindContents>
        )
      }
      popperModifiers={{
        preventOverflow: {
          boundariesElement: 'viewport',
        },
      }}
      // needs higher zindex than body .ui.dimmer in Base.less
      style={{padding: 0, left: '-15px', ...containerStyle}}
      on="click"
    />
  );
});

export default ColorPicker;
